import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  LinearProgress,
  MobileStepper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetBanquetBookingsMutation,
  useSaveBanquetBookingMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import {
  INITIAL_FORM,
  BookingFormState,
  computeFinancials,
  n,
  EMPTY_CUSTOMER,
} from "../BookingCreate/bookingTypes";
import Step1EventDetails from "../BookingCreate/Step1EventDetails";
import Step2Services from "../BookingCreate/Step2Services";
import Step3Addons from "../BookingCreate/Step3Addons";

// ── Steps ──────────────────────────────────────────────────────────────────────
const STEPS = ["Event Details", "Services & Financials", "Add-ons"];

// ── Validation (same as create) ────────────────────────────────────────────────
const validateStep1 = (form: BookingFormState): string | null => {
  if (!form.eventType) return "Event type is required";
  if (!form.expectedGuests || Number(form.expectedGuests) <= 0) return "Number of guests is required";
  if (Number(form.expectedGuests) > 900) return "Maximum capacity is 900 guests";
  if (!form.eventDate) return "Event date is required";
  if (!form.timeSlot)  return "Time slot is required";
  if (form.halls.length === 0) return "Select at least one hall";
  return null;
};

const validateStep2 = (form: BookingFormState): string | null => {
  if (n(form.guestCount) < 1) return "Guest count must be at least 1";
  return null;
};

// ── Map API booking to form state ──────────────────────────────────────────────
const toDateStr = (v: string | null | undefined): string => {
  if (!v) return "";
  return v.includes("T") ? v.split("T")[0] : v;
};

const mapCustomer = (c: any) => ({
  firstName:   c?.firstName   ?? "",
  lastName:    c?.lastName    ?? "",
  organization:c?.organization ?? "",
  email1:      c?.email1      ?? "",
  email2:      c?.email2      ?? "",
  address:     c?.address     ?? "",
  city:        c?.city        ?? "",
  province:    c?.province    ?? "ON",
  postalCode:  c?.postalCode  ?? "",
  phone1:      c?.phone1      ?? "",
  phone1Who:   c?.phone1Who   ?? "Cell",
  phone2:      c?.phone2      ?? "",
  phone2Who:   c?.phone2Who   ?? "Home",
  phone3:      c?.phone3      ?? "",
  phone3Who:   c?.phone3Who   ?? "Work",
});

const mapBookingToForm = (b: any): BookingFormState => ({
  banquetName:               b.banquetName               ?? "",
  agentName:                 b.agentName                 ?? "",
  eventType:                 b.eventType                 ?? "",
  eventDate:                 toDateStr(b.eventDate),
  timeSlot:                  b.timeSlot                  ?? "",
  expectedGuests:            String(b.expectedGuests     ?? ""),
  halls: (b.halls ?? []).map((h: any) => ({
    hallKey:      h.hallKey,
    hallLabel:    h.hallLabel,
    hallCapacity: h.hallCapacity,
  })),
  primaryCustomer:     mapCustomer(b.primaryCustomer),
  hasSecondaryCustomer: !!b.secondaryCustomer,
  secondaryCustomer:   b.secondaryCustomer
    ? mapCustomer(b.secondaryCustomer)
    : { ...EMPTY_CUSTOMER },
  publicNotes:  b.publicNotes  ?? "",
  privateNotes: b.privateNotes ?? "",

  guestCount:                String(b.guestCount                ?? 100),
  childrenCount:             String(b.childrenCount             ?? 0),
  guestMinimum:              String(b.guestMinimum              ?? 0),
  hallFlatFee:               String(b.hallFlatFee               ?? 0),
  hallPricePerPerson:        String(b.hallPricePerPerson        ?? 0),
  foodServiceOption:         b.foodServiceOption                ?? "",
  foodServicePricePerPerson: String(b.foodServicePricePerPerson ?? 0),
  barServiceOption:          b.barServiceOption                 ?? "",
  barServiceAmount:          String(b.barServiceAmount          ?? 0),
  barServiceType:            b.barServiceType                   ?? "",
  bartenderCount:            String(b.bartenderCount            ?? 0),
  bartenderAmount:           String(b.bartenderAmount           ?? 0),
  barPatrons:                String(b.barPatrons                ?? 0),
  barPackageOption:          b.barPackageOption                 ?? "",
  barPackagePricePerPerson:  String(b.barPackagePricePerPerson  ?? 0),
  lcboLicence:               b.lcboLicence                      ?? "",
  barHours:                  String(b.barHours                  ?? 0),
  menuOption:                b.menuOption                       ?? "",
  menuPricePerPerson:        String(b.menuPricePerPerson        ?? 0),
  adjustment:                String(b.adjustment                ?? 0),
  adjustmentReason:          b.adjustmentReason                 ?? "",
  serviceChargePercent:      String(b.serviceChargePercent      ?? 0),
  gratuityPercent:           String(b.gratuityPercent           ?? 0),
  securityDepositAmount:     String(b.securityDepositAmount     ?? 0),
  securityDepositMethod:     b.securityDepositMethod            ?? "Cash",
  securityDepositDate:       toDateStr(b.securityDepositDate),
  payments: (b.payments ?? [])
    .filter((p: any) => !p.isDelete)
    .map((p: any) => ({
      amount:      String(p.amount ?? ""),
      method:      p.method ?? "",
      paymentDate: toDateStr(p.paymentDate),
    })),
  addons: (b.addons ?? [])
    .filter((a: any) => !a.isDelete)
    .map((a: any) => ({
      service_id: a.service_id ?? null,
      itemName:   a.itemName   ?? "",
      category:   a.category   ?? "",
      quantity:   Number(a.quantity  ?? 1),
      unitPrice:  Number(a.unitPrice ?? 0),
      lineTotal:  Number(a.lineTotal ?? 0),
    })),
  vendorServices: (b.vendorServices ?? []).map((vs: any) => ({
    serviceId:       vs.serviceId       ?? vs.service_id ?? 0,
    serviceName:     vs.serviceName     ?? vs.service?.name ?? "",
    serviceCategory: vs.serviceCategory ?? vs.service?.category ?? "",
    vendorId:        vs.vendorId        ?? vs.vendor_id ?? 0,
    vendorName:      vs.vendorName      ?? vs.vendor?.name ?? "",
    pricingType:     vs.pricingType     ?? vs.pricing_type ?? "per_event",
    rate:            Number(vs.rate     ?? 0),
    quantity:        Number(vs.quantity ?? 1),
    hours:           Number(vs.hours   ?? 1),
    basePrice:       Number(vs.basePrice  ?? vs.base_price ?? 0),
    finalPrice:      Number(vs.finalPrice ?? vs.final_price ?? 0),
    isAdjustable:    vs.isAdjustable    ?? vs.is_adjustable ?? false,
    minPriceLimit:   vs.minPriceLimit   ?? vs.min_price_limit ?? null,
  })),
  selectedLEDs: b.selectedLEDs ?? [],
});

// ── Component ──────────────────────────────────────────────────────────────────
const BanquetBookingEdit = () => {
  const { id }    = useParams<{ id: string }>();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));
  const classes   = useStyles();
  const navigate  = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [activeStep, setActiveStep] = useState(0);
  const [form,       setForm]       = useState<BookingFormState>(INITIAL_FORM);
  const [bookingId,  setBookingId]  = useState<number | null>(null);
  const [bookingRef, setBookingRef] = useState("");
  const [status,     setStatus]     = useState("quote");
  const [isLoading,  setIsLoading]  = useState(true);

  const [getBookings]                              = useGetBanquetBookingsMutation();
  const [saveBooking, { isLoading: isSaving }]     = useSaveBanquetBookingMutation();

  // ── Load existing booking ──────────────────────────────────────────────────
  const loadBooking = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = (await (getBookings as any)({ id: Number(id) }).unwrap()) as any;
      if (res.status && Array.isArray(res.data) && res.data.length > 0) {
        const b = res.data[0];
        setBookingId(b.id);
        setBookingRef(b.bookingRef ?? `#${b.id}`);
        setStatus(b.status ?? "quote");
        setForm(mapBookingToForm(b));
      } else {
        showErrorMessage("Booking not found");
        navigate("/banquet/bookings");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to load booking");
      navigate("/banquet/bookings");
    } finally {
      setIsLoading(false);
    }
  }, [id, getBookings, showErrorMessage, navigate]);

  useEffect(() => { loadBooking(); }, [loadBooking]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (activeStep === 0) {
      const err = validateStep1(form);
      if (err) { showErrorMessage(err); return; }
    }
    if (activeStep === 1) {
      const err = validateStep2(form);
      if (err) { showErrorMessage(err); return; }
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (newStatus?: string) => {
    const err1 = validateStep1(form);
    const err2 = validateStep2(form);
    if (err1 || err2) { showErrorMessage(err1 ?? err2 ?? "Form error"); return; }

    const fin           = computeFinancials(form);
    const addonsSubtotal = form.addons.reduce((s, a) => s + a.lineTotal, 0);
    const subtotal      = fin.subtotal + addonsSubtotal;
    const hstAmount     = subtotal * 0.13;
    const grandTotal    = subtotal + hstAmount;

    const payload: any = {
      id:                        bookingId,
      banquetName:               form.banquetName,
      agentName:                 form.agentName,
      eventType:                 form.eventType,
      eventDate:                 form.eventDate,
      timeSlot:                  form.timeSlot,
      status:                    newStatus ?? status,
      expectedGuests:            n(form.expectedGuests),
      guestCount:                n(form.guestCount),
      childrenCount:             n(form.childrenCount),
      guestMinimum:              n(form.guestMinimum),
      barPatrons:                n(form.barPatrons),
      menuOption:                form.menuOption,
      menuPricePerPerson:        n(form.menuPricePerPerson),
      foodServiceOption:         form.foodServiceOption,
      foodServicePricePerPerson: n(form.foodServicePricePerPerson),
      barServiceOption:          form.barServiceOption,
      barServiceAmount:          n(form.barServiceAmount),
      barServiceType:            form.barServiceType,
      bartenderCount:            n(form.bartenderCount),
      bartenderAmount:           n(form.bartenderAmount),
      barPackageOption:          form.barPackageOption,
      barPackagePricePerPerson:  n(form.barPackagePricePerPerson),
      lcboLicence:               form.lcboLicence,
      barHours:                  n(form.barHours),
      hallFlatFee:               n(form.hallFlatFee),
      hallPricePerPerson:        n(form.hallPricePerPerson),
      hallSubtotal:              fin.hallSubtotal,
      foodSubtotal:              fin.foodSubtotal,
      barSubtotal:               fin.barSubtotal,
      addonsSubtotal,
      subtotal,
      adjustment:                n(form.adjustment),
      adjustmentReason:          form.adjustmentReason,
      serviceChargePercent:      n(form.serviceChargePercent),
      serviceChargeAmount:       fin.serviceChargeAmount,
      gratuityPercent:           n(form.gratuityPercent),
      gratuityAmount:            fin.gratuityAmount,
      hstAmount,
      grandTotal,
      securityDepositAmount:     n(form.securityDepositAmount),
      securityDepositMethod:     form.securityDepositMethod,
      securityDepositDate:       form.securityDepositDate || undefined,
      publicNotes:               form.publicNotes,
      privateNotes:              form.privateNotes,
      halls:                     form.halls,
      primaryCustomer: { ...form.primaryCustomer, customerType: "primary" },
      secondaryCustomer: form.hasSecondaryCustomer
        ? { ...form.secondaryCustomer, customerType: "secondary" }
        : undefined,
      payments: form.payments
        .filter((p) => n(p.amount) > 0)
        .map((p) => ({
          amount:      n(p.amount),
          method:      p.method,
          paymentDate: p.paymentDate || undefined,
        })),
      addons: form.addons.map((a) => ({
        service_id: a.service_id,
        itemName:   a.itemName,
        category:   a.category,
        quantity:   a.quantity,
        unitPrice:  a.unitPrice,
      })),
    };

    try {
      const res = (await (saveBooking as any)(payload).unwrap()) as any;
      if (res.status) {
        showMessage(res.message ?? "Booking updated successfully");
        navigate(`/banquet/booking/${bookingId}`);
      } else {
        showErrorMessage(res.message ?? "Failed to update booking");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? err?.message ?? "Failed to update booking");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Container maxWidth="xl" disableGutters={isMobile} sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
        <LinearProgress sx={{ mt: 2 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters={isMobile} sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
      <Box sx={{ pb: { xs: 10, sm: 12 } }}>

        {/* ── Page heading ── */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            color: "text.primary",
            fontSize: { xs: 16, sm: 18, md: 20 },
            mb: 2,
          }}
        >
          <EditNoteIcon
            sx={{ mr: 1, width: { xs: 22, sm: 26, md: 28 }, height: { xs: 22, sm: 26, md: 28 } }}
          />
          Edit Booking — {bookingRef}
        </Typography>

        {/* ── Desktop Stepper ── */}
        {!isMobile && (
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* ── Step Content ── */}
        <Card className={classes.card} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {isMobile && (
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 2 }}>
              Step {activeStep + 1} of {STEPS.length}: {STEPS[activeStep]}
            </Typography>
          )}

          {activeStep === 0 && (
            <Step1EventDetails
              form={form}
              setForm={setForm}
              excludeBookingId={bookingId ?? undefined}
            />
          )}
          {activeStep === 1 && (
            <Step2Services form={form} setForm={setForm} />
          )}
          {activeStep === 2 && (
            <Step3Addons form={form} setForm={setForm} />
          )}
        </Card>

        {/* ── Navigation ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowLeftIcon />}
            onClick={
              activeStep === 0
                ? () => navigate(`/banquet/booking/${bookingId}`)
                : handleBack
            }
            disabled={isSaving}
          >
            {activeStep === 0 ? "Cancel" : "Back"}
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            {activeStep < STEPS.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<KeyboardArrowRightIcon />}
                className={classes.addButton}
                onClick={handleNext}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                className={classes.addButton}
                onClick={() => handleSubmit()}
                disabled={isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />
                }
              >
                {isSaving ? "Saving…" : "Save Changes"}
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Mobile stepper dots ── */}
        {isMobile && (
          <MobileStepper
            variant="dots"
            steps={STEPS.length}
            position="static"
            activeStep={activeStep}
            sx={{ mt: 2, bgcolor: "transparent", justifyContent: "center" }}
            nextButton={null}
            backButton={null}
          />
        )}
      </Box>
    </Container>
  );
};

export default BanquetBookingEdit;
